import {NavigationItemConfigType} from '@rdcomponents/app/routing/routing-type';

export const FAVORITES_SECTION_KEY = 'favorites';

/* eslint-disable no-undefined */
const NO_PARENT: string | undefined = undefined;

function isMainSection(parentPath: string | undefined): boolean {
    return parentPath === NO_PARENT;
}

export function getFirstPagePath(item: NavigationItemConfigType): string | undefined {
    const path = item.path ?? item.key;

    if (!path) {
        return undefined;
    }

    if (item.redirectPath) {
        return item.redirectPath;
    }

    if (item.children && item.children.length > 0) {
        const firstChild = item.children[0];

        if (firstChild) {
            return getFirstPagePath(firstChild) ?? (firstChild.path ?? firstChild.key)?.toString();
        }
    }

    return path;
}

type ItemWithWeightType = {
    item: NavigationItemConfigType;
    path: string;
    weight: number;
    isMainSection: boolean;
    parentPath: string | undefined;
};

function collectItemsRecursive(
    items: Array<NavigationItemConfigType>,
    parentPath: string | undefined,
    startIndex: number
): {result: Array<ItemWithWeightType>; nextIndex: number} {
    return items.reduce<{result: Array<ItemWithWeightType>; nextIndex: number}>(
        (accumulator, item) => {
            if (item.isOnlyRoute) {
                return accumulator;
            }

            const path = (item.path ?? item.key)?.toString();

            if (!path) {
                return accumulator;
            }

            const mainSection = isMainSection(parentPath);
            const newEntry = {
                item,
                path,
                weight: accumulator.nextIndex,
                isMainSection: mainSection,
                parentPath,
            };
            let nextIndex = accumulator.nextIndex + 1;
            let result = [...accumulator.result, newEntry];

            if (item.children && item.children.length > 0) {
                const childResult = collectItemsRecursive(item.children, path, nextIndex);

                result = [...result, ...childResult.result];
                nextIndex = childResult.nextIndex;
            }

            return {result, nextIndex};
        },
        {result: [], nextIndex: startIndex}
    );
}

export function getItemsWithWeights(items: Array<NavigationItemConfigType>): Array<ItemWithWeightType> {
    const {result} = collectItemsRecursive(items, NO_PARENT, 0);

    return result;
}

function findItemByPathRecursive(
    items: Array<NavigationItemConfigType>,
    targetPath: string,
    parentPath: string | undefined
): {item: NavigationItemConfigType; parentPath: string | undefined} | null {
    const foundDirect = items.find((navItem) => (navItem.path ?? navItem.key)?.toString() === targetPath);

    if (foundDirect) {
        return {item: foundDirect, parentPath};
    }

    const foundInChild = items
        .filter((navItem) => navItem.children && navItem.children.length > 0)
        .map((navItem) => {
            const path = (navItem.path ?? navItem.key)?.toString();

            return path ? findItemByPathRecursive(navItem.children ?? [], targetPath, path) : null;
        })
        .find(Boolean);

    return foundInChild ?? null;
}

export function findItemByPath(
    items: Array<NavigationItemConfigType>,
    targetPath: string
): {item: NavigationItemConfigType; parentPath: string | undefined} | null {
    return findItemByPathRecursive(items, targetPath, NO_PARENT);
}

function flatNavigationItem(
    navigationItem: NavigationItemConfigType,
    icon?: NavigationItemConfigType['icon']
): Array<NavigationItemConfigType> {
    const childrenItems =
        navigationItem.children?.flatMap?.((itemChildren) => {
            return flatNavigationItem(itemChildren, navigationItem?.icon ?? icon);
        }) || [];

    return [
        {
            ...navigationItem,
            icon,
        },
        ...childrenItems,
    ];
}

export function buildFavouritesAndDefaultNavigationMenu(
    navigationConfig: Array<NavigationItemConfigType>,
    favorites: Array<string>
): {
    favouriteMenuItems: Array<NavigationItemConfigType>;
    defaultMenuItems: Array<NavigationItemConfigType>;
} {
    const favouritesMap: Map<string, NavigationItemConfigType> = new Map<string, NavigationItemConfigType>();
    const topLevelNonFavouriteItems: Array<NavigationItemConfigType> = [];

    navigationConfig.forEach((item) => {
        if (item.path && favorites?.includes(item.path)) {
            favouritesMap.set(item.path, item);
        } else {
            topLevelNonFavouriteItems.push(item);
        }
    });

    topLevelNonFavouriteItems
        .flatMap((item) => {
            return flatNavigationItem(item);
        })
        .forEach((item) => {
            if (item.path && favorites?.includes(item.path)) {
                favouritesMap.set(item.path, item);
            }
        });

    const sortedFavouriteItems = favorites?.flatMap((favouritePath) => {
        const itemByPath = favouritesMap.get(favouritePath);

        if (itemByPath) {
            return [itemByPath];
        }

        return [];
    });

    return {
        favouriteMenuItems: sortedFavouriteItems,
        defaultMenuItems: topLevelNonFavouriteItems,
    };
}
